import { useEffect, useState } from 'react';
import './LoginHeader.css';

interface Props {
  /** Path to the KNKT wordmark SVG. */
  logoSrc?: string;
  /** Cycling taglines. Last item gets the gradient flourish (longer hold). */
  taglines: string[];
  /** Sentinel that marks the "everything" tagline — gets cyan→pink gradient
   *  + held a beat longer. Default: the last item in the array. */
  flagshipTagline?: string;
  /** Max wordmark width in px. Caps at viewport so it scales on phones. */
  maxLogoWidth?: number;
}

/**
 * Branded header for the auth shell — KNKT wordmark + a single line of
 * italic display-serif text that rolls through product-specific phrases.
 * Mirrors the pattern shipped on promo.knkt.io so login pages across the
 * platform feel like one product family.
 */
export default function LoginHeader({
  logoSrc = '/knkt-logo.svg',
  taglines,
  flagshipTagline,
  maxLogoWidth = 320,
}: Props) {
  const flagship = flagshipTagline ?? taglines[taglines.length - 1];
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<'in' | 'shown' | 'out'>('in');
  const current = taglines[idx] ?? '';
  const isFlagship = current === flagship;

  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    if (phase === 'in') t = setTimeout(() => setPhase('shown'), 400);
    else if (phase === 'shown') t = setTimeout(() => setPhase('out'), isFlagship ? 3500 : 2000);
    else /* out */          t = setTimeout(() => { setIdx((i) => (i + 1) % taglines.length); setPhase('in'); }, 350);
    return () => clearTimeout(t);
  }, [phase, idx, isFlagship, taglines.length]);

  // Cap the wordmark width to ~55vw so it scales gracefully on phones.
  const width = typeof window !== 'undefined'
    ? Math.min(window.innerWidth * 0.55, maxLogoWidth)
    : maxLogoWidth;

  return (
    <header className="login-header">
      <img
        src={logoSrc}
        alt="KNKT"
        width={width}
        className="login-header-mark"
      />
      <div className="login-tagline-wrap" aria-live="polite">
        <div
          className={`login-tagline${isFlagship ? ' is-everything' : ''}`}
          data-phase={phase}
        >
          {current}
        </div>
      </div>
    </header>
  );
}
